import React from "react";
import { Box, Typography } from "@mui/material";

export const emptyDeckForm = {
  name: "",
  description: "",
  tags: "",
  isPublic: true,
};

export const emptyCardForm = {
  front: "",
  back: "",
  example: "",
  ipa: "",
  audioUrl: "",
  audioUrls: [],
  audioRegion: "",
  partOfSpeech: "",
  definition: "",
  lexicalSource: "",
  note: "",
};

const deckAccents = ["#6d4aff", "#46a8e8", "#4fbf9f", "#f0b84d", "#e96d78"];

export const getDeckAccent = (index = 0) => deckAccents[index % deckAccents.length];

export const formatIPA = (ipa = "") => {
  const cleaned = ipa.toString().trim().replace(/^\/|\/$/g, "");
  return cleaned ? `/${cleaned}/` : "";
};

const speakWithBrowserVoice = (word) => {
  if (!window.speechSynthesis || !word) return;

  const utterance = new SpeechSynthesisUtterance(word);
  utterance.lang = "en-US";
  window.speechSynthesis.cancel();
  window.speechSynthesis.resume();
  window.speechSynthesis.speak(utterance);
};

const getGoogleDictionaryAudioUrls = (word = "") => {
  const normalizedWord = word
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  if (!normalizedWord) return [];

  const encodedWord = encodeURIComponent(normalizedWord);
  return [
    `https://ssl.gstatic.com/dictionary/static/sounds/20200429/${encodedWord}--_gb_1.mp3`,
    `https://ssl.gstatic.com/dictionary/static/sounds/20200429/${encodedWord}--_us_1.mp3`,
  ];
};

const normalizePlaybackAudioUrl = (audioUrl = "") => {
  const trimmed = audioUrl.toString().trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;

  if (trimmed.startsWith("/media/pronunciations/")) {
    return `https://api.dictionaryapi.dev${trimmed}`;
  }

  if (/^[^/?#]+\.(mp3|ogg|oga|wav|m4a)$/i.test(trimmed)) {
    return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(trimmed)}`;
  }

  return "";
};

const normalizePlaybackAudioUrls = (audioInput) => {
  const values = Array.isArray(audioInput) ? audioInput : [audioInput];
  const seen = new Set();

  return values
    .map(normalizePlaybackAudioUrl)
    .filter(Boolean)
    .filter((url) => {
      if (seen.has(url)) return false;
      seen.add(url);
      return true;
    });
};

const playAudioSource = (audioUrl) =>
  new Promise((resolve, reject) => {
    const audio = new Audio();
    const timeout = window.setTimeout(() => {
      reject(new Error("Audio load timed out"));
    }, 4500);

    const cleanup = () => {
      window.clearTimeout(timeout);
      audio.onerror = null;
      audio.onplaying = null;
    };

    audio.preload = "auto";
    audio.onplaying = () => {
      cleanup();
      resolve();
    };
    audio.onerror = () => {
      cleanup();
      reject(audio.error || new Error("Audio source is not supported"));
    };
    audio.src = audioUrl;

    const playPromise = audio.play();
    if (playPromise?.catch) {
      playPromise.catch((error) => {
        cleanup();
        reject(error);
      });
    }
  });

export const playPronunciation = (word, audioInput) => {
  const playableAudioUrls = normalizePlaybackAudioUrls([
    ...getGoogleDictionaryAudioUrls(word),
    ...(Array.isArray(audioInput) ? audioInput : [audioInput]),
  ]);

  if (!playableAudioUrls.length) {
    speakWithBrowserVoice(word);
    return;
  }

  const tryNext = (index = 0) => {
    const audioUrl = playableAudioUrls[index];
    if (!audioUrl) {
      speakWithBrowserVoice(word);
      return;
    }

    playAudioSource(audioUrl).catch((error) => {
      console.warn("[Lingualica] Audio playback failed, trying fallback.", {
        word,
        audioUrl,
        error,
      });
      tryNext(index + 1);
    });
  };

  tryNext();
};

const escapeRegExp = (value = "") =>
  value.toString().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getExampleLines = (example = "") =>
  example
    .toString()
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

const renderHighlightedText = (text = "", term = "") => {
  const keyword = term.trim();
  if (!keyword) return text;

  const parts = text.split(new RegExp(`(${escapeRegExp(keyword)})`, "ig"));

  return parts.map((part, index) =>
    part.toLowerCase() === keyword.toLowerCase() ? (
      <Box component="strong" key={`${part}-${index}`} sx={{ color: "var(--toeic-ink)", fontWeight: 800 }}>
        {part}
      </Box>
    ) : (
      part
    )
  );
};

export const ExampleLines = ({ example, term, sx }) => {
  const lines = getExampleLines(example);
  if (!lines.length) return null;

  return (
    <Box sx={{ display: "grid", gap: 0.65, ...sx }}>
      {lines.map((line, index) => (
        <Typography
          key={`${line}-${index}`}
          sx={{
            color: "var(--toeic-ink)",
            fontSize: 13.5,
            fontStyle: "italic",
            fontWeight: 500,
            lineHeight: 1.45,
          }}
        >
          {renderHighlightedText(line, term)}
        </Typography>
      ))}
    </Box>
  );
};

export const practicePhaseOrder = ["learn", "recall", "write"];

export const practicePhaseMeta = {
  learn: {
    label: "Learn",
    title: "Get familiar",
    hint: "Read the meaning, IPA, pronunciation and examples first.",
    accent: "#6d4aff",
    surface: "#faf7ff",
  },
  recall: {
    label: "Recall",
    title: "Recall before reveal",
    hint: "Say the meaning in your head, then reveal the answer.",
    accent: "#46a8e8",
    surface: "#f3fbff",
  },
  write: {
    label: "Write",
    title: "Type from memory",
    hint: "Use the meaning and examples, then type the word.",
    accent: "#35a86a",
    surface: "#f7fff9",
  },
};

export const normalizePracticeAnswer = (value = "") =>
  value.toString().trim().toLowerCase().replace(/\s+/g, " ");

export const shuffleFlashcards = (items = []) => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return shuffled;
};
